import axios from 'axios';
import { getHostKind } from '../utils/host';
import { getStoredItem, removeStoredItem, setStoredItem } from '../utils/storage';

const api = axios.create({
  baseURL: '/api',
});

const authApi = axios.create({
  baseURL: '/api',
});

let refreshPromise = null;

const getAccountRoutePrefix = () => {
  const hostKind = getHostKind();
  if (hostKind === 'id') return '/id/v1';
  if (hostKind === 'ops') return '/ops/v1';
  return '/acct-mgmt/v1';
};

const notifyAuthTokenUpdate = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('finbase.auth_tokens_updated'));
  }
};

const redirectToLoginForExpiredSession = () => {
  removeStoredItem('access_token');
  removeStoredItem('refresh_token');
  removeStoredItem('refresh_expires_at');
  removeStoredItem('session_expiry_warning_seconds');
  notifyAuthTokenUpdate();
  setStoredItem('finbase.auth_expired', '1');
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

const refreshAccessToken = async () => {
  const refreshToken = getStoredItem('refresh_token');
  if (!refreshToken) return false;

  if (!refreshPromise) {
    refreshPromise = authApi.post(`${getAccountRoutePrefix()}/auth/refresh`, { refresh_token: refreshToken })
      .then((response) => {
        const accessToken = response.data?.access_token;
        if (!accessToken) return false;
        setStoredItem('access_token', accessToken);
        if (response.data?.refresh_token) {
          setStoredItem('refresh_token', response.data.refresh_token);
        }
        if (response.data?.refresh_expires_at) {
          setStoredItem('refresh_expires_at', response.data.refresh_expires_at);
        }
        if (response.data?.session_expiry_warning_seconds) {
          setStoredItem('session_expiry_warning_seconds', String(response.data.session_expiry_warning_seconds));
        }
        notifyAuthTokenUpdate();
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

api.interceptors.request.use((config) => {
  const hostKind = getHostKind();
  if (typeof config.url === 'string' && config.url.startsWith('/acct-mgmt/v1')) {
    if (hostKind === 'id') {
      config.url = config.url.replace('/acct-mgmt/v1', '/id/v1');
    } else if (hostKind === 'ops') {
      config.url = config.url.replace('/acct-mgmt/v1', '/ops/v1');
    }
  }
  const token = getStoredItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const originalRequest = error.config || {};
    if (originalRequest.skipAuthRedirect) {
      return Promise.reject(error);
    }
    const isRefreshRequest = typeof originalRequest.url === 'string' && originalRequest.url.includes('/auth/refresh');
    if (status === 401 && getStoredItem('refresh_token') && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true;
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        redirectToLoginForExpiredSession();
        return Promise.reject(error);
      }
      const token = getStoredItem('access_token');
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return api.request(originalRequest);
    }
    if (status === 401) {
      redirectToLoginForExpiredSession();
    }
    return Promise.reject(error);
  }
);

export { api };
